'use client';

import React, { useState } from 'react';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { RadioGroup } from './radio-group';
import { LinkInput } from './link-input';
import { TextArea } from './text-area';
import { ScreenshotInput, ScreenshotValue } from './screenshot-input';
import type { ApplicationFormContent } from '@/lib/ui-content/application-form-content';
import { processContact } from '@/server/send-email';
import {
  ExploreFormError,
  ExploreFormSection,
  ExploreFormSuccess,
  ExploreSubmit,
  RequiredMark,
  exploreFieldClass,
  exploreLabelClass,
} from '@/components/explore/explore-form-fields';

type ApplicationFormProps = {
  lang: LocaleDetails;
  /** Resolved by the page on the server, so the bundle carries one language, not all 24. */
  content: ApplicationFormContent;
  position: string;
  positionId: string;
};

type Status = 'idle' | 'loading' | 'success' | 'error';

const labelClass = exploreLabelClass;
const inputClass = exploreFieldClass;

const initialFormData = {
  fname: '',
  last_name: '',
  email: '',
  phone: '',
  country: '',
  project: '',
  creative: '',
  hardest: '',
  why: '',
  superpower: '',
  live_link: '',
  linkedin: '',
  twitter: '',
  github: '',
  portfolio: '',
  desired_salary: '',
  start_date: '',
  hp: '',
};

export const ApplicationForm = ({ lang, content, position, positionId }: ApplicationFormProps) => {
  const [formData, setFormData] = useState<Record<string, string>>(initialFormData);
  const [screenshot, setScreenshot] = useState<ScreenshotValue>(null);
  const [status, setStatus] = useState<Status>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const result = await processContact({
        ...formData,
        position,
        positionId,
        locale: lang.path,
        formType: 'application',
        screenshot: screenshot ?? undefined,
      });

      if (result && result.ok === false) {
        setStatus('error');
        return;
      }

      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return <ExploreFormSuccess title={content.successTitle} body={content.successMessage} />;
  }

  return (
    <form className="space-y-10" onSubmit={handleSubmit}>
      {/* Honeypot: hidden from humans, bots tend to fill it. */}
      <input
        type="text"
        name="hp"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={formData.hp}
        onChange={handleChange}
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
      />

      <ExploreFormSection title={content.sections.about.title}>
        <div className="space-y-8">
          <div className="grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2">
            <div>
              <label htmlFor={content.fname.id} className={labelClass}>
                <RequiredMark />
                {content.fname.label}
              </label>
              <input
                type="text"
                id={content.fname.id}
                name={content.fname.id}
                required={content.fname.required}
                value={formData.fname}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor={content.lastName.id} className={labelClass}>
                <RequiredMark />
                {content.lastName.label}
              </label>
              <input
                type="text"
                id={content.lastName.id}
                name={content.lastName.id}
                required={content.lastName.required}
                value={formData.last_name}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor={content.email.id} className={labelClass}>
                <RequiredMark />
                {content.email.label}
              </label>
              <input
                type="email"
                id={content.email.id}
                name={content.email.id}
                required={content.email.required}
                value={formData.email}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor={content.phone.id} className={labelClass}>
                {content.phone.label}
              </label>
              <input
                type="tel"
                id={content.phone.id}
                name={content.phone.id}
                value={formData.phone}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
          </div>

          <RadioGroup
            question={content.country.question}
            name={content.country.name}
            options={content.country.options}
            required={content.country.required}
            onChange={handleChange}
          />
        </div>
      </ExploreFormSection>

      <ExploreFormSection title={content.sections.work.title}>
        <div className="space-y-8">
          <TextArea
            id={content.project.id}
            label={content.project.label}
            placeholder={content.project.placeholder}
            required={content.project.required}
            value={formData.project}
            onChange={handleTextAreaChange}
          />

          <TextArea
            id={content.creative.id}
            label={content.creative.label}
            placeholder={content.creative.placeholder}
            required={content.creative.required}
            value={formData.creative}
            onChange={handleTextAreaChange}
          />

          <TextArea
            id={content.hardest.id}
            label={content.hardest.label}
            placeholder={content.hardest.placeholder}
            required={content.hardest.required}
            value={formData.hardest}
            onChange={handleTextAreaChange}
          />

          <TextArea
            id={content.why.id}
            label={content.why.label}
            placeholder={content.why.placeholder}
            required={content.why.required}
            value={formData.why}
            onChange={handleTextAreaChange}
            rows={3}
          />

          <div>
            <label htmlFor={content.superpower.id} className={labelClass}>
              {content.superpower.label}
            </label>
            <input
              type="text"
              id={content.superpower.id}
              name={content.superpower.id}
              value={formData.superpower}
              onChange={handleChange}
              className={inputClass}
              placeholder={content.superpower.placeholder}
            />
          </div>

          <div>
            <label htmlFor={content.liveLink.id} className={labelClass}>
              {content.liveLink.label}
            </label>
            <input
              type="url"
              id={content.liveLink.id}
              name={content.liveLink.id}
              value={formData.live_link}
              onChange={handleChange}
              className={inputClass}
              placeholder={content.liveLink.placeholder}
            />
            {content.liveLink.helper && (
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{content.liveLink.helper}</p>
            )}
          </div>

          <ScreenshotInput
            label={content.screenshot.label}
            helper={content.screenshot.helper}
            dropHint={content.screenshot.dropHint}
            changeLabel={content.screenshot.changeLabel}
            removeLabel={content.screenshot.removeLabel}
            tooLargeMessage={content.screenshot.tooLarge}
            onChange={setScreenshot}
          />

          <div>
            <p className={labelClass}>{content.links.label}</p>
            <div className="grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2">
              {content.links.fields.map((field) => (
                <LinkInput
                  key={field.id}
                  id={field.id}
                  label={field.label}
                  prefix={field.prefix}
                  onChange={handleChange}
                  locale={lang}
                />
              ))}
            </div>
          </div>
        </div>
      </ExploreFormSection>

      <ExploreFormSection title={content.sections.logistics.title}>
        <div>
          <div className="grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2">
            <div>
              <label htmlFor={content.desiredSalary.id} className={labelClass}>
                {content.desiredSalary.label}
              </label>
              <input
                type="text"
                id={content.desiredSalary.id}
                name={content.desiredSalary.id}
                value={formData.desired_salary}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor={content.startDate.id} className={labelClass}>
                {content.startDate.label}
              </label>
              <input
                type="date"
                id={content.startDate.id}
                name={content.startDate.id}
                value={formData.start_date}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
          </div>
        </div>
      </ExploreFormSection>

      <div className="flex flex-wrap items-center gap-4 pt-2">
        <ExploreSubmit
          label={content.submit}
          loadingLabel={content.sending}
          loading={status === 'loading'}
        />
        {status === 'error' && <ExploreFormError message={content.errorMessage} />}
      </div>
    </form>
  );
};
